import {
  useCompetitorControllerGetCompetitorList,
  useCompetitorControllerGetSkuStat,
} from "@/api/types/main";
import { MarketPlace } from "@/api/types/main/schemas";
import { ChartBarMarketShareBySku } from "@/components/custom-ui/charts/bar.market.share.by.sku";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import NumeralDataWidget from "@/components/custom-ui/widgets/numeral.data.period.widget";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { getMarketplaceIcons } from "@/lib/utils";
import { CompetitorsLayoutOutletContext } from "@/pages/shared/services/competitors/layout";
import { CompetitorsProductTable } from "@/pages/shared/services/competitors/product/table";
import { useOutletContext, useParams } from "react-router-dom";

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center p-4 text-center font-medium text-destructive">
      {message}
    </div>
  );
}

export function CompetitorsProductLayout() {
  const { sku } = useParams();
  const { date } = useOutletContext<CompetitorsLayoutOutletContext>();

  const skuNumber = Number(sku);
  const isValidSku = Number.isFinite(skuNumber);
  const safeSku = isValidSku ? skuNumber : 0;
  const canFetch = isValidSku && Boolean(date);

  const {
    data: skuInfo,
    isLoading: isSkuInfoLoading,
    isError: isSkuInfoError,
    error: skuInfoError,
  } = useCompetitorControllerGetSkuStat(
    safeSku,
    { date },
    {
      query: {
        enabled: canFetch,
      },
    }
  );

  const {
    data: competitorList,
    isLoading: isCompetitorListLoading,
    isError: isCompetitorListError,
    error: competitorListError,
  } = useCompetitorControllerGetCompetitorList(
    safeSku,
    { date },
    {
      query: {
        enabled: canFetch,
      },
    }
  );

  if (!isValidSku) {
    return <ErrorState message="Некорректный SKU в адресной строке" />;
  }

  if (!date) {
    return <ErrorState message="Не выбрана дата для загрузки данных" />;
  }

  if (isSkuInfoLoading || isCompetitorListLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isSkuInfoError || isCompetitorListError) {
    return (
      <ErrorState
        message={`Ошибка при загрузке данных: ${
          skuInfoError?.toString() || competitorListError?.toString()
        }`}
      />
    );
  }

  if (!skuInfo) {
    return <ErrorState message="Информация по SKU не найдена" />;
  }
  const productDescription = skuInfo.description || "Нет описания";
  const articleName = skuInfo.articleName || "Без названия";

  const stats = [
    {
      value: skuInfo.competitorCount ?? 0,
      title: "Кол-во конкурентов SKU",
      description: "Количество конкурентов выбранного SKU",
      valueTag: "count",
      className: "xl:col-span-4",
    },
    {
      value: skuInfo.competitorBrandsCount ?? 0,
      title: "Кол-во брендов конкурентов",
      description: "Количество брендов конкурентов выбранного SKU",
      valueTag: "count",
      className: "xl:col-span-4",
    },
    {
      value: skuInfo.competitorAvgRating ?? 0,
      title: "Средний рейтинг конкурентов",
      description: "Средний рейтинг конкурентов выбранного SKU",
      valueTag: "none",
      className: "xl:col-span-4",
    },
    {
      value: skuInfo.orderCount ?? 0,
      title: "Кол-во заказов SKU",
      description: "Кол-во заказов выбранного SKU",
      valueTag: "count",
      className: "xl:col-span-3",
    },
    {
      value: skuInfo.orderShare ?? 0,
      title: "Доля заказов SKU",
      description: "Доля заказов от общего количества заказов конкурентов выбранного SKU",
      valueTag: "percent",
      className: "xl:col-span-3",
    },
    {
      value: skuInfo.revenue ?? 0,
      title: "Выручка SKU",
      description: "Выручка выбранного SKU",
      valueTag: "rub",
      className: "xl:col-span-3",
    },
    {
      value: skuInfo.revenueShare ?? 0,
      title: "Доля по выручке, SKU",
      description: "Доля по выручке от общей выручки конкурентов выбранного SKU",
      valueTag: "percent",
      className: "xl:col-span-3",
    },
  ] as const;

  return (
    <div className="h-full min-h-0 overflow-hidden p-2">
      <ScrollArea className="h-full min-h-0">
        <div className="flex min-h-full flex-col gap-2 pr-3">
          <section className="grid shrink-0 gap-3  bg-card  lg:grid-cols-[10rem_minmax(0,1fr)]">
            <Avatar className="h-full min-h-40 w-full rounded-md">
              <AvatarImage src={skuInfo.imageLink} alt={articleName} />
              <AvatarFallback className="rounded-md text-xs">
                {articleName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 space-y-2">
              <div className="flex min-w-0 items-start justify-between gap-2">
                <h1 className="min-w-0 truncate text-xl font-semibold">{articleName}</h1>

                {skuInfo.marketplaceLink && (
                  <a
                    href={skuInfo.marketplaceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Открыть товар на маркетплейсе"
                    className="shrink-0"
                  >
                    {getMarketplaceIcons(MarketPlace.WB)}
                  </a>
                )}
              </div>

              <div className="text-sm text-muted-foreground">SKU: {skuInfo.sku}</div>

              <ScrollArea className="h-28 rounded-md border bg-muted/20">
                <p className="whitespace-pre-wrap break-words p-3 text-sm leading-relaxed text-muted-foreground">
                  {productDescription}
                </p>
                <ScrollBar orientation="vertical" />
              </ScrollArea>
            </div>
          </section>

          <section className="grid shrink-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-12">
            {stats.map((stat) => (
              <NumeralDataWidget
                key={stat.title}
                value={stat.value}
                title={stat.title}
                description={stat.description}
                valueTag={stat.valueTag}
                className={stat.className}
              />
            ))}
          </section>

          <section className="h-96 shrink-0 ">
            <ChartBarMarketShareBySku sku={safeSku} />
          </section>

          <section className="min-h-[420px] flex-1 rounded-md border bg-card p-2">
            {competitorList ? (
              <CompetitorsProductTable
                tableData={competitorList}
                date={date}
                productSKU={safeSku}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Нет данных по конкурентам
              </div>
            )}
          </section>
        </div>

        <ScrollBar orientation="vertical" />
      </ScrollArea>
    </div>
  );
}
